// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Trash2,
  ArrowRightLeft,
  SquarePen,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  Building,
  MapPin,
  Map,
  LayoutGrid,
  Settings,
  Palette,
  Mail,
  Users,
  Check,
} from "lucide-react";
import tenantService from "../../services/tenantService";
import locationService from "../../services/locationService";
import { tenantSettingsService } from "../../services/tenantSettingsService";
import { userService } from "../../services/userService";
import { roleService } from "../../services/roleService";
import useCaseService from "../../services/useCaseService";
import { slugify } from "../../utils/locationFormHelpers";
import LoadingOverlay from "../../components/LoadingOverlay";
import InlineNotification from "../../components/InlineNotification";
import useInlineNotification from "../../hooks/useInlineNotification";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import toast from "react-hot-toast";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const mergeUseCases = (apiUseCases = [], savedUseCases = []) => {
  const savedById = Object.create(null);
  (Array.isArray(savedUseCases) ? savedUseCases : []).forEach((u) => {
    savedById[String(u.id ?? u.code)] = u;
  });
  return (Array.isArray(apiUseCases) ? apiUseCases : []).map((uc) => {
    const saved = savedById[String(uc.id)] ?? savedById[String(uc.code)];
    return {
      id: uc.id,
      code: uc.code,
      name: uc.name,
      enabled: saved?.enabled ?? true,
      approval_required: saved?.approval_required ?? false,
    };
  });
};

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #6366f1, #8b5cf6)",
  "linear-gradient(135deg, #f97316, #ef4444)",
  "linear-gradient(135deg, #14b8a6, #06b6d4)",
  "linear-gradient(135deg, #3b82f6, #6366f1)",
];

const PLAN_STYLES = {
  enterprise: "bg-primary-color-tint text-primary-color",
  professional: "bg-primary-color-tint text-primary-color",
  starter: "bg-primary-color-tint text-primary-color",
  default: "bg-primary-color-tint text-primary-color",
};

const DEFAULT_TENANT_SETTINGS = {
  branding: {
    logo_url: "",
    company_name: "",
    primary_color: "#6366F1",
    secondary_color: "#06B6D4",
  },
  email: {
    from_name: "",
    from_email: "",
    reply_to: "",
  },
  features: {
    api_access: false,
    sso_enabled: false,
    audit_logs: false,
    two_factor: false,
    webhooks: false,
  },
  booking: {
    auto_approval: false,
    cancellation_hours: "",
    max_advance_days: "",
    min_advance_hours: "",
    use_cases: [],
  },
  localization: {
    language: "de",
    timezone: "Europe/Berlin",
    date_format: "DD.MM.YYYY",
    time_format: "24h",
    currency: "EUR",
  },
};

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("edit");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [settingsModalTab, setSettingsModalTab] = useState("general");
  const [slugDirty, setSlugDirty] = useState(false);
  const [modalSettings, setModalSettings] = useState(DEFAULT_TENANT_SETTINGS);
  const [formData, setFormData] = useState({
    name: "",
    subdomain: "",
    slug: "",
    contact_email: "",
    contact_phone: "",
    description: "",
    is_active: true,
    status: "active",
    plan_id: "",
    from_template_id: "",
  });

  const [availableLocations, setAvailableLocations] = useState([]);
  const [tenantLocations, setTenantLocations] = useState([]);
  const [locationAssigning, setLocationAssigning] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);

  const [userPanelMode, setUserPanelMode] = useState(null);
  const [userActionSaving, setUserActionSaving] = useState(false);
  const [newTenantUserForm, setNewTenantUserForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    roles: [],
    status: "active",
  });
  const [availableRoles, setAvailableRoles] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [assignableUsersLoading, setAssignableUsersLoading] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [pendingMoveUserId, setPendingMoveUserId] = useState(null);
  const [pendingMoveTargetTenantId, setPendingMoveTargetTenantId] =
    useState("");
  const [movingUserId, setMovingUserId] = useState(null);
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [editingRolesUserId, setEditingRolesUserId] = useState(null);
  const [editingRoles, setEditingRoles] = useState([]);
  const [savingRolesUserId, setSavingRolesUserId] = useState(null);
  const [createStep, setCreateStep] = useState("details");
  const [createdTenantId, setCreatedTenantId] = useState(null);

  const [overviewStats, setOverviewStats] = useState({
    activeTenants: 0,
    totalBookings: 0,
    activeUsers: 0,
  });
  const [hierarchyCounts, setHierarchyCounts] = useState({
    locations: 0,
    scenarios: 0,
    areas: 0,
  });

  const { notification, showSuccess, showError } = useInlineNotification();

  const [plans, setPlans] = useState([]);
  const [pendingLogoFile, setPendingLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  // Blob URL of the tenant's currently saved logo (from the picture API).
  const [savedLogoUrl, setSavedLogoUrl] = useState(null);
  const logoFileInputRef = useRef(null);

  useEffect(() => {
    if (!pendingLogoFile) {
      setLogoPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingLogoFile);
    setLogoPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingLogoFile]);

  // Revoke the saved-logo blob URL whenever it changes or the page unmounts.
  useEffect(() => {
    return () => {
      if (savedLogoUrl) URL.revokeObjectURL(savedLogoUrl);
    };
  }, [savedLogoUrl]);

  const fetchTenants = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit, sort_by: sortBy, sort_order: sortOrder };
      if (search?.trim()) params.search = search.trim();
      const response = await tenantService.getTenants(params);
      const items =
        response?.items ??
        response?.tenants ??
        response?.data ??
        response?.results ??
        [];
      const totalCount =
        response?.total ??
        response?.count ??
        (Array.isArray(items) ? items.length : 0);
      setTenants(Array.isArray(items) ? items : []);
      setTotal(Number(totalCount) || 0);
      if (
        typeof (response?.active_total ?? response?.active_count) === "number"
      ) {
        setOverviewStats((prev) => ({
          ...prev,
          activeTenants:
            response.active_total ??
            response.active_count ??
            prev.activeTenants,
        }));
      }
    } catch (err) {
      console.error("Error fetching tenants:", err);
      showError("Fehler beim Laden der Mandanten");
      setTenants([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortBy, sortOrder, search, showError]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tenantService.getPlans();
      setPlans(response?.items ?? response?.tenants ?? response?.data ?? []);
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    let cancelled = false;
    tenantService
      .getTenantManagementStats()
      .then((data) => {
        if (cancelled || !data) return;
        if (typeof data.active_tenants === "number") {
          setOverviewStats((prev) => ({
            ...prev,
            activeTenants: data.active_tenants,
          }));
        }
        if (typeof data.total_bookings === "number") {
          setOverviewStats((prev) => ({
            ...prev,
            totalBookings: data.total_bookings,
          }));
        }
        if (typeof data.active_users === "number") {
          setOverviewStats((prev) => ({
            ...prev,
            activeUsers: data.active_users,
          }));
        }
        if (typeof data.total_locations === "number") {
          setHierarchyCounts((prev) => ({
            ...prev,
            locations: data.total_locations,
          }));
        }
        if (typeof data.total_scenarios === "number") {
          setHierarchyCounts((prev) => ({
            ...prev,
            scenarios: data.total_scenarios,
          }));
        }
        if (typeof data.areas === "number") {
          setHierarchyCounts((prev) => ({ ...prev, areas: data.areas }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const openCreate = () => {
    setFormData({
      name: "",
      subdomain: "",
      slug: "",
      contact_email: "",
      contact_phone: "",
      description: "",
      is_active: true,
      status: "active",
      plan_id: "",
    });
    setEditingTenant(null);
    setCreateStep("details");
    setCreatedTenantId(null);
    setTenantLocations([]);
    setShowCreateModal(true);
    locationService
      .getLocations({ limit: 200 })
      .then((res) => {
        const locs = res?.items || res?.locations || res?.data || [];
        setAvailableLocations(Array.isArray(locs) ? locs : []);
      })
      .catch(() => {});
  };

  const openSettings = (tenant) => {
    setPanelMode("settings");
    setEditingTenant(tenant);
    setPendingLogoFile(null);
    setSavedLogoUrl(null);
    setFormData({
      name: tenant?.name ?? "",
      subdomain: tenant?.subdomain ?? "",
      slug: tenant?.slug ?? "",
      contact_email: tenant?.contact_email ?? "",
      contact_phone: tenant?.contact_phone ?? "",
      description: tenant?.description ?? "",
      is_active: tenant?.is_active ?? true,
      status: tenant?.status ?? "active",
      plan_id: tenant?.plan_id ? String(tenant.plan_id) : "",
    });
    setSlugDirty(Boolean(tenant?.slug?.trim()));
    setModalSettings(DEFAULT_TENANT_SETTINGS);
    setSettingsModalTab("general");
    setTenantLocations([]);
    setTenantUsers([]);
    setPanelOpen(true);

    (async () => {
      try {
        const rawId = tenant.tenant_id ?? tenant.id;
        const [full, config, locRes, availLocs, ucRes, logoUrl] =
          await Promise.all([
            tenantService.getTenantById(rawId),
            tenantSettingsService.getTenantConfig(rawId),
            tenantService.getTenantLocations(rawId).catch(() => []),
            locationService.getLocations({ limit: 200 }).catch(() => ({})),
            useCaseService.listUseCases().catch(() => []),
            tenantSettingsService.getTenantLogoUrl(rawId),
          ]);
        setSavedLogoUrl(logoUrl);
        const apiUseCases = Array.isArray(ucRes?.items)
          ? ucRes.items
          : Array.isArray(ucRes)
            ? ucRes
            : [];
        setEditingTenant(full);
        setFormData({
          name: full?.name ?? "",
          subdomain: full?.subdomain ?? "",
          slug: full?.slug ?? "",
          contact_email: full?.contact_email ?? "",
          contact_phone: full?.contact_phone ?? "",
          description: full?.description ?? "",
          is_active: full?.is_active ?? true,
          status: full?.status ?? "active",
          plan_id: full?.plan_id ? String(full.plan_id) : "",
        });
        setSlugDirty(Boolean(full?.slug?.trim()));
        const tLocs = Array.isArray(locRes)
          ? locRes
          : locRes?.items || locRes?.locations || locRes?.data || [];
        setTenantLocations(tLocs);
        const aLocs =
          availLocs?.items || availLocs?.locations || availLocs?.data || [];
        setAvailableLocations(Array.isArray(aLocs) ? aLocs : []);
        const cfg = config && typeof config === "object" ? config : {};
        setModalSettings({
          ...DEFAULT_TENANT_SETTINGS,
          branding: {
            ...DEFAULT_TENANT_SETTINGS.branding,
            ...(cfg.branding || {}),
            company_name:
              cfg.branding?.company_name ??
              full?.name ??
              DEFAULT_TENANT_SETTINGS.branding.company_name,
          },
          email: {
            ...DEFAULT_TENANT_SETTINGS.email,
            ...(cfg.email || {}),
          },
          features: {
            ...DEFAULT_TENANT_SETTINGS.features,
            ...(cfg.features || {}),
          },
          booking: {
            ...DEFAULT_TENANT_SETTINGS.booking,
            ...(cfg.booking || {}),
            use_cases: mergeUseCases(apiUseCases, cfg.booking?.use_cases),
          },
          localization: {
            ...DEFAULT_TENANT_SETTINGS.localization,
            ...(cfg.localization || {}),
          },
        });
        await loadTenantUsers(rawId, full);
      } catch (err) {
        console.error("Error loading tenant:", err);
        toast.error("Mandant konnte nicht geladen werden");
      }
    })();
  };

  const loadTenantUsers = async (tenantId, tenantMeta = null) => {
    if (!tenantId) return;
    setTenantUsersLoading(true);
    try {
      const response = await userService.getTenantUsers();
      const allUsers = response?.users || response?.items || response || [];
      const usersArray = Array.isArray(allUsers) ? allUsers : [];
      const tenantIdStr = String(tenantId);
      const tenantName = tenantMeta?.name;
      const tenantSubdomain = tenantMeta?.subdomain;

      const filteredUsers = usersArray.filter((user) => {
        const directTenantId =
          user?.tenant_id ??
          user?.tenant ??
          user?.tenantId ??
          user?.tenant?.id ??
          user?.tenant?.tenant_id;

        if (directTenantId != null && String(directTenantId) === tenantIdStr) {
          return true;
        }

        if (tenantName && user?.tenant_name === tenantName) {
          return true;
        }

        if (
          tenantSubdomain &&
          (user?.tenant_subdomain === tenantSubdomain ||
            user?.tenant_slug === tenantSubdomain)
        ) {
          return true;
        }

        return false;
      });

      setTenantUsers(filteredUsers);
    } catch (error) {
      console.error("Error loading tenant users:", error);
      setTenantUsers([]);
    } finally {
      setTenantUsersLoading(false);
    }
  };

  const ensureRolesLoaded = async () => {
    if (availableRoles.length > 0) return;
    try {
      const resp = await roleService.getRoles();
      const items = resp?.items || resp || [];
      setAvailableRoles(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error("Could not load roles for inline create:", err);
      setAvailableRoles([]);
    }
  };

  const loadAssignableUsers = async (currentTenantId) => {
    if (!currentTenantId) return;
    setAssignableUsersLoading(true);
    try {
      const response = await userService.getTenantUsers();
      const all = response?.users || response?.items || response || [];
      const tid = String(currentTenantId);
      setAssignableUsers(
        (Array.isArray(all) ? all : []).filter(
          (u) => String(u?.tenant_id ?? u?.tenant?.id ?? "") !== tid,
        ),
      );
    } catch (err) {
      console.error("Could not load assignable users:", err);
      setAssignableUsers([]);
    } finally {
      setAssignableUsersLoading(false);
    }
  };

  const openUserPanel = async (mode) => {
    setUserPanelMode(mode);
    if (mode === "create") {
      setNewTenantUserForm({
        first_name: "",
        last_name: "",
        email: "",
        roles: [],
        status: "active",
      });
      ensureRolesLoaded();
    } else if (mode === "assign") {
      setAssignSearch("");
      loadAssignableUsers(editingTenant?.tenant_id ?? editingTenant?.id);
    }
  };

  const closeUserPanel = () => {
    setUserPanelMode(null);
    setUserActionSaving(false);
  };

  const handleCreateTenantUser = async (e) => {
    e.preventDefault();
    const tenantId = editingTenant?.tenant_id ?? editingTenant?.id;
    if (!tenantId) return;
    if (
      !newTenantUserForm.first_name.trim() ||
      !newTenantUserForm.last_name.trim() ||
      !newTenantUserForm.email.trim() ||
      !newTenantUserForm.roles[0]
    ) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    setUserActionSaving(true);
    try {
      await userService.postTenantUser({
        first_name: newTenantUserForm.first_name.trim(),
        last_name: newTenantUserForm.last_name.trim(),
        email: newTenantUserForm.email.trim().toLowerCase(),
        roles: newTenantUserForm.roles,
        status: newTenantUserForm.status,
        tenant_id: String(tenantId),
      });
      showSuccess("Nutzer erfolgreich erstellt und zugewiesen.");
      await loadTenantUsers(tenantId, editingTenant);
      closeUserPanel();
    } catch (err) {
      console.error("Inline create user failed:", err);
      toast.error(
        err?.response?.data?.detail || "Nutzer konnte nicht erstellt werden.",
      );
    } finally {
      setUserActionSaving(false);
    }
  };

  const openRolesEditor = async (user) => {
    const uid = user?.user_id ?? user?.id;
    setPendingMoveUserId(null);
    setPendingDeleteUserId(null);
    await ensureRolesLoaded();
    setEditingRoles(Array.isArray(user.roles) ? [...user.roles] : []);
    setEditingRolesUserId(uid);
  };

  const closeRolesEditor = () => {
    setEditingRolesUserId(null);
    setEditingRoles([]);
  };

  const handleSaveUserRoles = async (user) => {
    const userId = user?.user_id ?? user?.id;
    if (!userId) return;
    if (editingRoles.length === 0) {
      toast.error("Mindestens eine Rolle ist erforderlich.");
      return;
    }
    setSavingRolesUserId(userId);
    try {
      await userService.putTenantUser(userId, {
        roles: editingRoles,
        require_keycloak_sync: true,
      });
      showSuccess("Rollen aktualisiert.");
      await loadTenantUsers(
        editingTenant?.tenant_id ?? editingTenant?.id,
        editingTenant,
      );
      closeRolesEditor();
    } catch (err) {
      console.error("Update user roles failed:", err);
      toast.error(
        err?.response?.data?.detail ||
          "Rollen konnten nicht aktualisiert werden.",
      );
    } finally {
      setSavingRolesUserId(null);
    }
  };

  const handleDeleteTenantUser = async (user) => {
    const userId = user?.user_id ?? user?.id;
    if (!userId) return;
    setDeletingUserId(userId);
    try {
      await userService.deleteTenantUser(userId);
      showSuccess(
        `${[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "Nutzer"} wurde gelöscht.`,
      );
      await loadTenantUsers(
        editingTenant?.tenant_id ?? editingTenant?.id,
        editingTenant,
      );
      setPendingDeleteUserId(null);
    } catch (err) {
      console.error("Inline delete user failed:", err);
      toast.error(
        err?.response?.data?.detail || "Nutzer konnte nicht gelöscht werden.",
      );
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleMoveTenantUser = async (user, targetTenantId) => {
    const userId = user?.user_id ?? user?.id;
    if (!userId || !targetTenantId) return;
    setMovingUserId(userId);
    try {
      await userService.putTenantUser(userId, {
        tenant_id: String(targetTenantId),
      });
      const targetName =
        tenants.find(
          (m) => String(m.tenant_id ?? m.id) === String(targetTenantId),
        )?.name || "anderem Mandanten";
      showSuccess(
        `${[user.first_name, user.last_name].filter(Boolean).join(" ") || user.email || "Nutzer"} wurde nach ${targetName} verschoben.`,
      );
      await loadTenantUsers(
        editingTenant?.tenant_id ?? editingTenant?.id,
        editingTenant,
      );
      setPendingMoveUserId(null);
      setPendingMoveTargetTenantId("");
    } catch (err) {
      console.error("Inline move user failed:", err);
      toast.error(
        err?.response?.data?.detail || "Nutzer konnte nicht verschoben werden.",
      );
    } finally {
      setMovingUserId(null);
    }
  };

  const handleAssignExistingUser = async (user) => {
    const tenantId = editingTenant?.tenant_id ?? editingTenant?.id;
    const userId = user?.user_id ?? user?.id;
    if (!tenantId || !userId) return;
    setUserActionSaving(true);
    try {
      await userService.putTenantUser(userId, {
        tenant_id: String(tenantId),
      });
      showSuccess(
        `${user.first_name || user.email} wurde diesem Mandanten zugewiesen.`,
      );
      await loadTenantUsers(tenantId, editingTenant);
      closeUserPanel();
    } catch (err) {
      console.error("Inline assign user failed:", err);
      toast.error(
        err?.response?.data?.detail || "Nutzer konnte nicht zugewiesen werden.",
      );
    } finally {
      setUserActionSaving(false);
    }
  };

  const closePanel = () => {
    setPanelOpen(false);
    setPanelMode("edit");
    setEditingTenant(null);
    setPendingLogoFile(null);
    setTenantLocations([]);
    setTenantUsers([]);
    setFormData({
      name: "",
      subdomain: "",
      slug: "",
      contact_email: "",
      contact_phone: "",
      description: "",
      is_active: true,
      status: "active",
      plan_id: "",
    });
  };

  const SETTINGS_TABS = [
    {
      id: "general",
      icon: <Building className="w-4 h-4" />,
      label: "Allgemein",
    },
    {
      id: "branding",
      icon: <Palette className="w-4 h-4" />,
      label: "Design",
    },
    {
      id: "email",
      icon: <Mail className="w-4 h-4" />,
      label: "Kommunikation",
    },
    // { id: "features", icon: <ToggleLeft className="w-4 h-4" />, label: "Funktionen" },
    {
      id: "users_roles",
      icon: <Users className="w-4 h-4" />,
      label: "Nutzer/Rollen",
    },
    {
      id: "locations",
      icon: <MapPin className="w-4 h-4" />,
      label: "Standorte",
    },
    {
      id: "use_cases",
      icon: <LayoutGrid className="w-4 h-4" />,
      label: "Nutzungsarten",
    },
  ];

  const handleAddLocationClick = () => {
    window.location.href = "/admin/standorte";
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const created = await tenantService.createTenant({
        name: formData.name.trim(),
        subdomain:
          formData.subdomain?.trim() ||
          formData.name.trim().toLowerCase().replace(/\s+/g, "-"),
        slug: formData.slug?.trim() || slugify(formData.name) || undefined,
        contact_email: formData.contact_email?.trim() || undefined,
        contact_phone: formData.contact_phone?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        is_active: (formData.status || "active") === "active",
        status: formData.status || "active",
        plan_id: formData.plan_id || null,
        from_template_id: formData.from_template_id || undefined,
      });
      showSuccess("Mandant erfolgreich erstellt");
      const newTenantId = created?.tenant_id || created?.id;
      if (newTenantId) {
        setCreatedTenantId(newTenantId);
        setCreateStep("locations");
        setTenantLocations([]);
      } else {
        setShowCreateModal(false);
        setFormData({
          name: "",
          subdomain: "",
          slug: "",
          contact_email: "",
          contact_phone: "",
          description: "",
          is_active: true,
          status: "active",
          plan_id: "",
          from_template_id: "",
        });
      }
      fetchTenants();
    } catch (err) {
      console.error("Error saving tenant:", err);
      toast.error(
        err?.response?.data?.message ?? "Fehler beim Erstellen des Mandanten",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    if (!editingTenant) return;
    setSaving(true);
    try {
      const id = editingTenant.tenant_id ?? editingTenant.id;
      await tenantService.updateTenant(id, {
        name: formData.name.trim(),
        subdomain: formData.subdomain?.trim() || undefined,
        slug: formData.slug?.trim() || slugify(formData.name) || undefined,
        contact_email: formData.contact_email?.trim() || undefined,
        contact_phone: formData.contact_phone?.trim() || undefined,
        description: formData.description?.trim() || undefined,
        is_active: (formData.status || "active") === "active",
        status: formData.status || "active",
        plan_id: formData.plan_id || null,
      });
      showSuccess("Mandant erfolgreich aktualisiert");
      closePanel();
      fetchTenants();
    } catch (err) {
      console.error("Error saving tenant:", err);
      toast.error(
        err?.response?.data?.message ??
          "Fehler beim Aktualisieren des Mandanten",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    if (!editingTenant) return;

    setSaving(true);
    try {
      const tenantIdForSettings = editingTenant.tenant_id ?? editingTenant.id;
      let settingsToSave = { ...modalSettings };

      if (pendingLogoFile) {
        // Logo is stored via the picture API (keyed by tenant_id), separate
        // from the tenant config JSON.
        await tenantSettingsService.uploadTenantLogo(
          tenantIdForSettings,
          pendingLogoFile,
        );
        const url =
          await tenantSettingsService.getTenantLogoUrl(tenantIdForSettings);
        setSavedLogoUrl(url);
        setPendingLogoFile(null);
        if (logoFileInputRef.current) {
          logoFileInputRef.current.value = "";
        }
      }

      await tenantSettingsService.updateTenantConfig(
        tenantIdForSettings,
        settingsToSave,
      );
      showSuccess("Einstellungen erfolgreich gespeichert");
    } catch (err) {
      console.error("Error saving tenant config:", err);
      toast.error(
        err?.response?.data?.message ??
          "Fehler beim Speichern der Einstellungen",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLogoFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setPendingLogoFile(file);
    if (logoFileInputRef.current) {
      logoFileInputRef.current.value = "";
    }
  };

  const handleAssignLocation = async (locationId, role, isPrimary) => {
    const tenantId =
      createdTenantId || (editingTenant?.tenant_id ?? editingTenant?.id);
    if (!tenantId || !locationId) return;
    setLocationAssigning(true);
    try {
      await tenantService.assignLocationToTenant(tenantId, {
        location_id: Number(locationId),
        role: role || "operator",
        is_primary: isPrimary || false,
      });
      const res = await tenantService.getTenantLocations(tenantId);
      const locs = Array.isArray(res)
        ? res
        : res?.items || res?.locations || res?.data || [];
      setTenantLocations(locs);
      toast.success("Standort zugewiesen");
    } catch (err) {
      console.error("Error assigning location:", err);
      toast.error(err?.response?.data?.message ?? "Fehler beim Zuweisen");
    } finally {
      setLocationAssigning(false);
    }
  };

  const handleRemoveLocation = async (locationId) => {
    const tenantId =
      createdTenantId || (editingTenant?.tenant_id ?? editingTenant?.id);
    if (!tenantId || !locationId) return;
    const numLocId = Number(locationId);
    setLocationAssigning(true);
    try {
      await tenantService.removeLocationFromTenant(tenantId, numLocId);
      setTenantLocations((prev) =>
        prev.filter((l) => Number(l.location_id || l.id) !== numLocId),
      );
      toast.success("Standort entfernt");
    } catch (err) {
      console.error("Error removing location:", err);
      toast.error(err?.response?.data?.message ?? "Fehler beim Entfernen");
    } finally {
      setLocationAssigning(false);
    }
  };

  const LocationAssignForm = ({ assignedIds }) => {
    const [selLoc, setSelLoc] = useState("");
    const [selRole, setSelRole] = useState("operator");
    const [selPrimary, setSelPrimary] = useState(false);
    const numAssignedIds = assignedIds.map(Number);
    const unassigned = availableLocations.filter(
      (loc) => !numAssignedIds.includes(Number(loc.location_id || loc.id)),
    );
    return (
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-gray-50 dark:bg-slate-700/50 border border-gray-200 dark:border-slate-600">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Standort
          </label>
          <select
            value={selLoc}
            onChange={(e) => setSelLoc(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
          >
            <option value="">— Standort wählen —</option>
            {unassigned.map((loc) => (
              <option
                key={loc.location_id || loc.id}
                value={loc.location_id || loc.id}
              >
                {loc.name}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[120px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Rolle
          </label>
          <select
            value={selRole}
            onChange={(e) => setSelRole(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
          >
            <option value="owner">Owner</option>
            <option value="operator">Operator</option>
            <option value="partner">Partner</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="loc-primary"
            checked={selPrimary}
            onChange={(e) => setSelPrimary(e.target.checked)}
            className="rounded border-gray-300 dark:border-slate-500"
          />
          <label
            htmlFor="loc-primary"
            className="text-xs text-gray-600 dark:text-slate-400"
          >
            Primär
          </label>
        </div>
        <button
          type="button"
          disabled={!selLoc || locationAssigning}
          onClick={() => {
            handleAssignLocation(selLoc, selRole, selPrimary);
            setSelLoc("");
            setSelRole("operator");
            setSelPrimary(false);
          }}
          className="px-4 py-2 rounded-lg bg-primary-color text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          {locationAssigning ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Plus className="w-4 h-4" />
          )}
          Hinzufügen
        </button>
      </div>
    );
  };

  const handleDeleteClick = (tenant) => setDeleteConfirm(tenant);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.tenant_id ?? deleteConfirm.id;
    setSaving(true);
    try {
      await tenantService.deleteTenant(id);
      showSuccess("Mandant erfolgreich gelöscht");
      setDeleteConfirm(null);
      closePanel();
      fetchTenants();
    } catch (err) {
      console.error("Error deleting tenant:", err);
      toast.error(
        err?.response?.data?.message ?? "Fehler beim Löschen des Mandanten",
      );
    } finally {
      setSaving(false);
    }
  };

  const _handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="relative">
      <GlobalAdminHeader
        title="Mandanten Management"
        subtitle="Verwalten Sie Ihre Mandanten und deren Einstellungen"
        buttons={[
          {
            text: "Mandant anlegen",
            icon: <Plus className="h-4 w-4" />,
            onClick: openCreate,
            className:
              "bg-primary-color text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2",
          },
        ]}
      />
      <div className="flex flex-col gap-4 px-4 lg:px-4 py-4 text-left dark:text-slate-200 w-full">
        <InlineNotification notification={notification} />

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden p-5">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-1 border-r border-gray-200 dark:border-slate-600 pr-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Objekthierarchie
              </p>
              <div className="space-y-2">
                {[
                  {
                    icon: <Building className="w-5 h-5" />,
                    label: "Mandanten",
                    sublabel: "Tenant-Ebene",
                    count: total,
                    active: true,
                  },
                  {
                    icon: <MapPin className="w-5 h-5" />,
                    label: "Standorte",
                    sublabel: "Location-Ebene",
                    count: hierarchyCounts.locations,
                    active: false,
                  },
                  {
                    icon: <Map className="w-5 h-5" />,
                    label: "Szenarien",
                    sublabel: "Scenario-Ebene",
                    count: hierarchyCounts.scenarios,
                    active: false,
                  },
                  {
                    icon: <LayoutGrid className="w-5 h-5" />,
                    label: "Flächen",
                    sublabel: "Space-Ebene",
                    count: hierarchyCounts.areas,
                    active: false,
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg ${
                      item.active
                        ? "bg-primary-color-tint text-primary-color"
                        : "text-gray-600 dark:text-slate-400"
                    }`}
                  >
                    {item.icon}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-medium ${
                          item.active
                            ? "text-primary-color dark:text-primary-color"
                            : "text-gray-900 dark:text-slate-100"
                        }`}
                      >
                        {item.label}
                      </p>
                      <p className="text-[10px] text-gray-500 dark:text-slate-500 truncate">
                        {item.sublabel}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-semibold ${
                        item.active
                          ? "bg-primary-color text-white"
                          : "bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-400"
                      }`}
                    >
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 pl-0 lg:pl-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                Übersicht
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
                  <div className="absolute inset-0 bg-gradient-primary-secondary-tint transition-opacity duration-300" />
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Aktive Mandanten
                  </p>
                  <p className="text-2xl font-bold primary-color">
                    {overviewStats.activeTenants}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
                  <div className="absolute inset-0 bg-gradient-primary-secondary-tint transition-opacity duration-300" />
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Gesamtbuchungen
                  </p>
                  <p className="text-2xl font-bold primary-color">
                    {overviewStats.totalBookings}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl border-primary-color overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-[0px_12px_40px_rgba(99,102,241,0.15)]">
                  <div className="absolute inset-0 bg-gradient-primary-secondary-tint transition-opacity duration-300" />
                  <p className="text-sm text-gray-600 dark:text-slate-400">
                    Aktive Benutzer
                  </p>
                  <p className="text-2xl font-bold primary-color">
                    {overviewStats.activeUsers}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="search"
            placeholder="Mandanten durchsuchen..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:placeholder-slate-400 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
          />
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg bg-white text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} pro Seite
              </option>
            ))}
          </select>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              Mandantenliste
            </h3>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {total} Mandant{total !== 1 ? "en" : ""} im System
            </p>
          </div>

          {loading ? (
            <LoadingOverlay loading />
          ) : tenants.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-slate-400 text-sm">
              Keine Mandanten gefunden.
            </div>
          ) : (
            <>
              <div className="p-4 space-y-3">
                {tenants.map((tenant, index) => {
                  const id = tenant.tenant_id ?? tenant.id;
                  const rawPlan =
                    tenant.plan_code ?? tenant.plan_name ?? "starter";
                  const planKey = String(rawPlan).toLowerCase();
                  const planLabel = String(rawPlan).toUpperCase();
                  const avatarGradient =
                    AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length];
                  const createdDate = tenant.created_at
                    ? new Date(tenant.created_at).toLocaleDateString("de-DE")
                    : null;
                  const locationsCount = tenant.total_locations_count ?? 0;
                  const usersCount = tenant.total_users_count ?? 0;
                  const bookingsCount =
                    tenant.bookings_count ?? tenant.total_bookings_count ?? 0;

                  return (
                    <div
                      key={id}
                      className="flex flex-wrap items-center gap-4 lg:gap-6 p-5 rounded-xl bg-white dark:bg-slate-800/50 border border-gray-200 dark:border-slate-600 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-lg shrink-0"
                        style={{ background: avatarGradient }}
                      >
                        {(tenant.name || "T").charAt(0).toUpperCase()}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-slate-100 truncate">
                            {tenant.name ?? "—"}
                          </h4>
                          {(() => {
                            const statusConfig = {
                              active: {
                                label: "Aktiv",
                                bg: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200",
                                dot: "bg-green-500",
                              },
                              inactive: {
                                label: "Inaktiv",
                                bg: "bg-gray-100 dark:bg-slate-600 text-gray-700 dark:text-slate-300",
                                dot: "bg-gray-400 dark:bg-slate-400",
                              },
                              suspended: {
                                label: "Gesperrt",
                                bg: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200",
                                dot: "bg-red-500",
                              },
                              demo: {
                                label: "Demo",
                                bg: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200",
                                dot: "bg-amber-500",
                              },
                            };
                            const cfg =
                              statusConfig[tenant.status] ||
                              statusConfig.inactive;
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${cfg.bg}`}
                              >
                                <span
                                  className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`}
                                />
                                {cfg.label}
                              </span>
                            );
                          })()}
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full text-white ${
                              PLAN_STYLES[planKey] || PLAN_STYLES.default
                            }`}
                          >
                            {planLabel}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                          <span className="font-mono bg-gray-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-xs">
                            {tenant.subdomain ?? "—"}
                          </span>
                          {createdDate && (
                            <>
                              <span className="mx-1">•</span>
                              <span>Erstellt am {createdDate}</span>
                            </>
                          )}
                        </p>
                      </div>

                      <div className="flex items-center gap-4 sm:gap-6 shrink-0">
                        <div className="text-center">
                          <p className="text-2xl font-bold primary-color">
                            {locationsCount}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            Standorte
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold primary-color">
                            {usersCount}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            Benutzer
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold primary-color">
                            {bookingsCount}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            Buchungen
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => openSettings(tenant)}
                          className="p-2 rounded-lg hover:bg-primary-color-tint primary-color transition-colors"
                          title="Einstellungen"
                        >
                          <Settings className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(tenant)}
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400 transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/50 text-sm">
                  <span className="text-gray-600 dark:text-slate-400">
                    {total} Mandant{total !== 1 ? "en" : ""}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-gray-700 dark:text-slate-300">
                      Seite {page} von {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page >= totalPages}
                      className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {panelOpen && editingTenant && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closePanel}
            aria-hidden
          />
          <div
            className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col dark:border dark:border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-8 py-6 border-b border-gray-100 dark:border-slate-700 shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/5 via-cyan-500/5 to-emerald-500/5 dark:from-indigo-500/10 dark:via-cyan-500/10 dark:to-emerald-500/10" />
              <div className="relative flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                    Mandanteneinstellungen
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                    {editingTenant.name ?? "—"} •{" "}
                    {editingTenant.subdomain ?? editingTenant.subdomain ?? "—"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closePanel}
                  className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-600 text-gray-500 dark:text-slate-400 transition-colors"
                  disabled={saving}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Tabs – only in settings mode; no General tab */}
            {panelMode === "settings" && (
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-700/30 shrink-0">
                <div className="flex flex-wrap gap-1">
                  {SETTINGS_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setSettingsModalTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                        settingsModalTab === tab.id
                          ? "bg-primary-color-tint primary-color"
                          : "text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-600"
                      }`}
                    >
                      {tab.icon}
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <form
              id="tenant-settings-form"
              onSubmit={
                settingsModalTab === "general"
                  ? handleEditSubmit
                  : handleSettingsSubmit
              }
              className="p-8 overflow-y-auto flex-1 min-h-0"
              style={{ scrollbarWidth: "thin", maxHeight: "60vh" }}
            >
              {/* "Allgemein" tab: basic tenant data; other tabs: config settings */}
              {panelMode === "settings" && settingsModalTab === "general" && (
                <div className="space-y-5">
                  {/* Identity row — Name, Subdomain, Slug (3 columns). */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => {
                          const name = e.target.value;
                          setFormData((prev) => ({
                            ...prev,
                            name,
                            slug: slugDirty ? prev.slug : slugify(name),
                          }));
                        }}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Subdomain
                      </label>
                      <input
                        type="text"
                        value={formData.subdomain}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            subdomain: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Slug
                      </label>
                      <input
                        type="text"
                        value={formData.slug}
                        onChange={(e) => {
                          setSlugDirty(true);
                          setFormData((prev) => ({
                            ...prev,
                            slug: e.target.value,
                          }));
                        }}
                        placeholder="wird aus dem Namen erzeugt"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                  </div>

                  {/* Contact row — Telefon, E-Mail (2 columns). */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Kontakttelefon
                      </label>
                      <input
                        type="tel"
                        value={formData.contact_phone}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            contact_phone: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Kontakt-E-Mail
                      </label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            contact_email: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Plan
                      </label>
                      <select
                        value={formData.plan_id || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            plan_id: e.target.value,
                          }))
                        }
                        disabled={saving || plans.length === 0}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      >
                        <option value="">
                          {plans.length === 0
                            ? "Keine Pläne verfügbar"
                            : "Kein Plan zugewiesen"}
                        </option>
                        {plans.map((plan) => {
                          const optionId =
                            plan.id ??
                            plan.plan_id ??
                            plan.uuid ??
                            plan.key ??
                            null;
                          if (!optionId) return null;
                          const label =
                            plan.name ??
                            plan.plan_name ??
                            plan.code ??
                            String(optionId);
                          return (
                            <option key={optionId} value={String(optionId)}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                      <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">
                        Aktueller Tarifplan für diesen Mandanten.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      >
                        <option value="active">Aktiv</option>
                        <option value="inactive">Inaktiv</option>
                        <option value="suspended">Gesperrt</option>
                        <option value="demo">Demo</option>
                      </select>
                    </div>
                  </div>

                  {/* Description — full width at the bottom. */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                      Beschreibung
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      rows={3}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                    />
                  </div>
                </div>
              )}

              {panelMode === "settings" && settingsModalTab === "branding" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                        Logo
                      </label>
                      {savedLogoUrl || pendingLogoFile ? (
                        <div className="flex items-center gap-4 flex-wrap">
                          <div className="h-16 w-16 rounded-xl overflow-hidden border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 flex items-center justify-center shrink-0">
                            {pendingLogoFile && logoPreviewUrl ? (
                              <img
                                src={logoPreviewUrl}
                                alt="Neues Logo"
                                className="max-h-14 max-w-full object-contain"
                              />
                            ) : (
                              <img
                                src={savedLogoUrl}
                                alt="Tenant Logo"
                                className="max-h-14 max-w-full object-contain"
                              />
                            )}
                          </div>
                          <div className="flex flex-col gap-2">
                            {pendingLogoFile && (
                              <p className="text-xs text-gray-600 dark:text-slate-300">
                                {pendingLogoFile.name} – wird beim Speichern
                                übernommen
                              </p>
                            )}
                            <button
                              type="button"
                              onClick={() => logoFileInputRef.current?.click()}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-color text-white hover:opacity-90 w-fit"
                            >
                              {savedLogoUrl
                                ? "Logo ersetzen"
                                : "Logo hochladen"}
                            </button>
                            {pendingLogoFile && (
                              <button
                                type="button"
                                onClick={() => setPendingLogoFile(null)}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600 w-fit"
                              >
                                Auswahl aufheben
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500 dark:text-slate-400">
                            Noch kein Logo hochgeladen.
                          </p>
                          <button
                            type="button"
                            onClick={() => logoFileInputRef.current?.click()}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-color text-white hover:opacity-90"
                          >
                            Logo hochladen
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        ref={logoFileInputRef}
                        onChange={handleLogoFileSelect}
                        className="hidden"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Firmenname
                      </label>
                      <input
                        type="text"
                        value={modalSettings.branding.company_name}
                        onChange={(e) =>
                          setModalSettings((prev) => ({
                            ...prev,
                            branding: {
                              ...prev.branding,
                              company_name: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Primärfarbe
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="color"
                          value={modalSettings.branding.primary_color}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              branding: {
                                ...prev.branding,
                                primary_color: e.target.value,
                              },
                            }))
                          }
                          className="h-12 w-16 rounded-xl border border-gray-200 dark:border-slate-600 cursor-pointer shrink-0"
                        />
                        <input
                          type="text"
                          value={modalSettings.branding.primary_color}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              branding: {
                                ...prev.branding,
                                primary_color: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 font-mono text-sm focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Sekundärfarbe
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="color"
                          value={modalSettings.branding.secondary_color}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              branding: {
                                ...prev.branding,
                                secondary_color: e.target.value,
                              },
                            }))
                          }
                          className="h-12 w-16 rounded-xl border border-gray-200 dark:border-slate-600 cursor-pointer shrink-0"
                        />
                        <input
                          type="text"
                          value={modalSettings.branding.secondary_color}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              branding: {
                                ...prev.branding,
                                secondary_color: e.target.value,
                              },
                            }))
                          }
                          className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 font-mono text-sm focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-2xl bg-primary-color-tint border border-primary-color">
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      <span className="font-semibold primary-color">
                        Vorschau:
                      </span>{" "}
                      Die Markenfarben werden auf der öffentlichen Buchungsseite
                      und in E-Mail-Vorlagen angewendet.
                    </p>
                  </div>
                </div>
              )}

              {panelMode === "settings" && settingsModalTab === "email" && (
                <div className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Absendername
                      </label>
                      <input
                        type="text"
                        value={modalSettings.email.from_name}
                        onChange={(e) =>
                          setModalSettings((prev) => ({
                            ...prev,
                            email: {
                              ...prev.email,
                              from_name: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Absender-E-Mail
                      </label>
                      <input
                        type="email"
                        value={modalSettings.email.from_email}
                        onChange={(e) =>
                          setModalSettings((prev) => ({
                            ...prev,
                            email: {
                              ...prev.email,
                              from_email: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                        Antwort-Adresse
                      </label>
                      <input
                        type="email"
                        value={modalSettings.email.reply_to}
                        onChange={(e) =>
                          setModalSettings((prev) => ({
                            ...prev,
                            email: {
                              ...prev.email,
                              reply_to: e.target.value,
                            },
                          }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)] transition-all"
                      />
                    </div>
                  </div>
                  <p className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200">
                    <span className="font-semibold">Hinweis:</span> Für
                    individuelle SMTP-Konfiguration wenden Sie sich bitte an den
                    Support.
                  </p>

                  <div className="border-t border-gray-200 dark:border-slate-600" />

                  <div className="space-y-5">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                      Lokalisierung
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Standardsprache
                        </label>
                        <select
                          value={modalSettings.localization.language}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              localization: {
                                ...prev.localization,
                                language: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        >
                          <option value="de">Deutsch</option>
                          <option value="en">English</option>
                          <option value="fr">Français</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Zeitzone
                        </label>
                        <select
                          value={modalSettings.localization.timezone}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              localization: {
                                ...prev.localization,
                                timezone: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        >
                          <option value="Europe/Berlin">Europe/Berlin</option>
                          <option value="Europe/London">Europe/London</option>
                          <option value="Europe/Paris">Europe/Paris</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Währung
                        </label>
                        <select
                          value={modalSettings.localization.currency}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              localization: {
                                ...prev.localization,
                                currency: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        >
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="USD">USD ($)</option>
                          <option value="CHF">CHF (Fr.)</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Datumsformat
                        </label>
                        <select
                          value={modalSettings.localization.date_format}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              localization: {
                                ...prev.localization,
                                date_format: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        >
                          <option value="DD.MM.YYYY">DD.MM.YYYY</option>
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Zeitformat
                        </label>
                        <select
                          value={modalSettings.localization.time_format}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              localization: {
                                ...prev.localization,
                                time_format: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        >
                          <option value="24h">24 Stunden</option>
                          <option value="12h">12 Stunden (AM/PM)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {panelMode === "settings" &&
                settingsModalTab === "users_roles" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                        Nutzer/Rollen
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            openUserPanel(
                              userPanelMode === "assign" ? null : "assign",
                            )
                          }
                          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                            userPanelMode === "assign"
                              ? "border-primary-color bg-primary-color-tint primary-color"
                              : "border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700"
                          }`}
                        >
                          Bestehenden Nutzer zuweisen
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openUserPanel(
                              userPanelMode === "create" ? null : "create",
                            )
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-color text-white text-sm font-medium hover:opacity-90 transition-opacity"
                        >
                          <Plus className="w-4 h-4" />
                          Nutzer erstellen
                        </button>
                      </div>
                    </div>

                    {userPanelMode === "create" && (
                      <div
                        onKeyDown={(e) => {
                          if (
                            e.key === "Enter" &&
                            !userActionSaving &&
                            e.target.tagName !== "TEXTAREA"
                          ) {
                            e.preventDefault();
                            handleCreateTenantUser(e);
                          }
                        }}
                        className="p-4 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 space-y-3"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            Neuen Nutzer für{" "}
                            {editingTenant?.name || "diesen Mandanten"}{" "}
                            erstellen
                          </h4>
                          <button
                            type="button"
                            onClick={closeUserPanel}
                            aria-label="Schließen"
                            className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <input
                            type="text"
                            placeholder="Vorname *"
                            value={newTenantUserForm.first_name}
                            onChange={(e) =>
                              setNewTenantUserForm((p) => ({
                                ...p,
                                first_name: e.target.value,
                              }))
                            }
                            required
                            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
                          />
                          <input
                            type="text"
                            placeholder="Nachname *"
                            value={newTenantUserForm.last_name}
                            onChange={(e) =>
                              setNewTenantUserForm((p) => ({
                                ...p,
                                last_name: e.target.value,
                              }))
                            }
                            required
                            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
                          />
                        </div>
                        <input
                          type="email"
                          placeholder="Email *"
                          value={newTenantUserForm.email}
                          onChange={(e) =>
                            setNewTenantUserForm((p) => ({
                              ...p,
                              email: e.target.value,
                            }))
                          }
                          required
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
                        />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                            Rollen * (Mehrfachauswahl)
                          </label>
                          <div className="flex flex-wrap gap-2 p-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 max-h-40 overflow-y-auto">
                            {availableRoles.length === 0 ? (
                              <span className="text-xs text-gray-400 dark:text-slate-500">
                                Rollen werden geladen …
                              </span>
                            ) : (
                              availableRoles.map((r) => {
                                const checked =
                                  newTenantUserForm.roles.includes(r.name);
                                return (
                                  <label
                                    key={r.role_id || r.id || r.name}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs cursor-pointer border transition-colors ${
                                      checked
                                        ? "bg-primary-color-tint primary-color border-primary-color/40 font-medium"
                                        : "bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={checked}
                                      onChange={() =>
                                        setNewTenantUserForm((p) => ({
                                          ...p,
                                          roles: checked
                                            ? p.roles.filter(
                                                (x) => x !== r.name,
                                              )
                                            : [...p.roles, r.name],
                                        }))
                                      }
                                    />
                                    {r.name}
                                  </label>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <select
                          value={newTenantUserForm.status}
                          onChange={(e) =>
                            setNewTenantUserForm((p) => ({
                              ...p,
                              status: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
                        >
                          <option value="active">Aktiv</option>
                          <option value="inactive">Inaktiv</option>
                          <option value="pending">Ausstehend</option>
                          <option value="suspended">Gesperrt</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          Der Nutzer erhält per E-Mail eine Einladung, sein
                          Passwort festzulegen.
                        </p>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={(e) => handleCreateTenantUser(e)}
                            disabled={userActionSaving}
                            className="px-4 py-2 rounded-lg bg-primary-color text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
                          >
                            {userActionSaving && (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            )}
                            Nutzer erstellen
                          </button>
                        </div>
                      </div>
                    )}

                    {userPanelMode === "assign" && (
                      <div className="p-4 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                            Bestehenden Nutzer diesem Mandanten zuweisen
                          </h4>
                          <button
                            type="button"
                            onClick={closeUserPanel}
                            aria-label="Schließen"
                            className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="search"
                          value={assignSearch}
                          onChange={(e) => setAssignSearch(e.target.value)}
                          placeholder="Nach Name oder Email suchen…"
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 text-sm"
                        />
                        {assignableUsersLoading ? (
                          <p className="text-sm text-gray-500 dark:text-slate-400">
                            Nutzer werden geladen…
                          </p>
                        ) : (
                          (() => {
                            const q = assignSearch.trim().toLowerCase();
                            const list = assignableUsers.filter((u) => {
                              if (!q) return true;
                              const hay =
                                `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
                              return hay.includes(q);
                            });
                            if (list.length === 0) {
                              return (
                                <p className="text-sm text-gray-500 dark:text-slate-400">
                                  Keine passenden Nutzer gefunden.
                                </p>
                              );
                            }
                            return (
                              <div className="max-h-64 overflow-y-auto space-y-1.5">
                                {list.map((u) => (
                                  <button
                                    key={u.user_id || u.id || u.email}
                                    type="button"
                                    disabled={userActionSaving}
                                    onClick={() => handleAssignExistingUser(u)}
                                    className="w-full flex items-center justify-between p-2.5 rounded-lg border border-gray-100 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 text-left disabled:opacity-50"
                                  >
                                    <span className="min-w-0">
                                      <span className="block text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                        {[u.first_name, u.last_name]
                                          .filter(Boolean)
                                          .join(" ") ||
                                          u.username ||
                                          "Unbekannt"}
                                      </span>
                                      <span className="block text-xs text-gray-500 dark:text-slate-400 truncate">
                                        {u.email} · {u.tenant_name || "—"}
                                      </span>
                                    </span>
                                    <span className="text-xs primary-color font-medium shrink-0">
                                      Zuweisen →
                                    </span>
                                  </button>
                                ))}
                              </div>
                            );
                          })()
                        )}
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          Hinweis: Ein Nutzer kann immer nur einem Mandanten
                          angehören — die Zuweisung verschiebt ihn aus seinem
                          aktuellen Mandanten.
                        </p>
                      </div>
                    )}
                    <div className="p-4 rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                          Zugewiesene Nutzer
                        </h4>
                        <button
                          type="button"
                          onClick={() =>
                            loadTenantUsers(
                              editingTenant?.tenant_id ?? editingTenant?.id,
                              editingTenant,
                            )
                          }
                          className="text-xs font-medium primary-color hover:underline"
                        >
                          Aktualisieren
                        </button>
                      </div>
                      {tenantUsersLoading ? (
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          Nutzer werden geladen...
                        </p>
                      ) : tenantUsers.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-slate-400">
                          Keine Nutzer für diesen Mandanten gefunden.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {tenantUsers.map((user) => {
                            const uid = user.user_id ?? user.id;
                            const isMovePending = pendingMoveUserId === uid;
                            const isDeletePending = pendingDeleteUserId === uid;
                            const isMoving = movingUserId === uid;
                            const isDeleting = deletingUserId === uid;
                            const isEditingRoles = editingRolesUserId === uid;
                            const isSavingRoles = savingRolesUserId === uid;
                            const currentTenantId =
                              editingTenant?.tenant_id ?? editingTenant?.id;
                            const openMove = () => {
                              setEditingRolesUserId(null);
                              setPendingDeleteUserId(null);
                              setPendingMoveUserId(uid);
                              setPendingMoveTargetTenantId("");
                            };
                            const openDelete = () => {
                              setEditingRolesUserId(null);
                              setPendingMoveUserId(null);
                              setPendingMoveTargetTenantId("");
                              setPendingDeleteUserId(uid);
                            };
                            return (
                              <div
                                key={uid || user.email}
                                className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-slate-700 gap-3"
                              >
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">
                                    {[user.first_name, user.last_name]
                                      .filter(Boolean)
                                      .join(" ") ||
                                      user.username ||
                                      "Unbekannt"}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                    {user.email || "Keine E-Mail"}
                                  </p>
                                </div>
                                {isMovePending ? (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <select
                                      value={pendingMoveTargetTenantId}
                                      onChange={(e) =>
                                        setPendingMoveTargetTenantId(
                                          e.target.value,
                                        )
                                      }
                                      disabled={isMoving}
                                      className="px-2 py-1 text-xs border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-md disabled:opacity-50"
                                    >
                                      <option value="">
                                        Verschieben nach…
                                      </option>
                                      {tenants
                                        .map((m) => ({
                                          id: m.tenant_id ?? m.id,
                                          name: m.name,
                                        }))
                                        .filter(
                                          (m) =>
                                            m.id &&
                                            String(m.id) !==
                                              String(currentTenantId),
                                        )
                                        .map((m) => (
                                          <option key={m.id} value={m.id}>
                                            {m.name}
                                          </option>
                                        ))}
                                    </select>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setPendingMoveUserId(null);
                                        setPendingMoveTargetTenantId("");
                                      }}
                                      disabled={isMoving}
                                      className="px-2 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
                                    >
                                      Abbrechen
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleMoveTenantUser(
                                          user,
                                          pendingMoveTargetTenantId,
                                        )
                                      }
                                      disabled={
                                        isMoving || !pendingMoveTargetTenantId
                                      }
                                      className="px-2 py-1 rounded-md text-xs font-medium bg-primary-color text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                                    >
                                      {isMoving && (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      )}
                                      Verschieben
                                    </button>
                                  </div>
                                ) : isDeletePending ? (
                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className="text-xs text-gray-600 dark:text-slate-300">
                                      Endgültig löschen?
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setPendingDeleteUserId(null)
                                      }
                                      disabled={isDeleting}
                                      className="px-2 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
                                    >
                                      Abbrechen
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDeleteTenantUser(user)
                                      }
                                      disabled={isDeleting}
                                      className="px-2 py-1 rounded-md text-xs font-medium bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-1"
                                    >
                                      {isDeleting && (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      )}
                                      Löschen
                                    </button>
                                  </div>
                                ) : isEditingRoles ? (
                                  <div className="flex flex-col items-end gap-2 shrink-0 max-w-[60%]">
                                    <div className="flex flex-wrap justify-end gap-1.5">
                                      {availableRoles.length === 0 ? (
                                        <span className="text-xs text-gray-400 dark:text-slate-500">
                                          Rollen werden geladen …
                                        </span>
                                      ) : (
                                        availableRoles.map((r) => {
                                          const checked = editingRoles.includes(
                                            r.name,
                                          );
                                          return (
                                            <label
                                              key={r.role_id || r.id || r.name}
                                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs cursor-pointer border transition-colors ${
                                                checked
                                                  ? "bg-primary-color-tint primary-color border-primary-color/40 font-medium"
                                                  : "bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700"
                                              }`}
                                            >
                                              <input
                                                type="checkbox"
                                                className="sr-only"
                                                checked={checked}
                                                onChange={() =>
                                                  setEditingRoles((prev) =>
                                                    prev.includes(r.name)
                                                      ? prev.filter(
                                                          (x) => x !== r.name,
                                                        )
                                                      : [...prev, r.name],
                                                  )
                                                }
                                              />
                                              {r.name}
                                            </label>
                                          );
                                        })
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        onClick={closeRolesEditor}
                                        disabled={isSavingRoles}
                                        className="px-2 py-1 rounded-md text-xs font-medium border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
                                      >
                                        Abbrechen
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleSaveUserRoles(user)
                                        }
                                        disabled={isSavingRoles}
                                        className="px-2 py-1 rounded-md text-xs font-medium bg-primary-color text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
                                      >
                                        {isSavingRoles && (
                                          <Loader2 className="w-3 h-3 animate-spin" />
                                        )}
                                        Speichern
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 shrink-0">
                                    {Array.isArray(user.roles) &&
                                    user.roles.length > 0 ? (
                                      <div className="flex flex-wrap justify-end gap-1 max-w-[260px]">
                                        {user.roles.map((roleName) => (
                                          <span
                                            key={roleName}
                                            className="px-2 py-1 rounded-full text-xs font-medium bg-primary-color-tint primary-color"
                                          >
                                            {roleName}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300">
                                        Keine Rolle
                                      </span>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => openRolesEditor(user)}
                                      title="Rollen bearbeiten"
                                      aria-label="Rollen bearbeiten"
                                      className="p-1.5 rounded-md text-gray-400 hover:text-primary-color hover:bg-primary-color-tint transition-colors"
                                    >
                                      <SquarePen className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={openMove}
                                      title="In anderen Mandanten verschieben"
                                      aria-label="In anderen Mandanten verschieben"
                                      className="p-1.5 rounded-md text-gray-400 hover:text-primary-color hover:bg-primary-color-tint transition-colors"
                                    >
                                      <ArrowRightLeft className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={openDelete}
                                      title="Nutzer endgültig löschen"
                                      aria-label="Nutzer endgültig löschen"
                                      className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {panelMode === "settings" && settingsModalTab === "use_cases" && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                      Allgemeine Buchungsregeln
                    </h3>
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-slate-700/50">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-slate-100">
                          Automatische Genehmigung
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          Buchungen werden ohne manuelle Prüfung bestätigt
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer shrink-0">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={!!modalSettings.booking.auto_approval}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              booking: {
                                ...prev.booking,
                                auto_approval: e.target.checked,
                              },
                            }))
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-color rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-color dark:peer-checked:bg-primary-color/40" />
                      </label>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Stornierungsfrist (Stunden)
                        </label>
                        <input
                          type="number"
                          value={modalSettings.booking.cancellation_hours}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              booking: {
                                ...prev.booking,
                                cancellation_hours: Number(e.target.value) || 0,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          Zeitraum für kostenlose Stornierung
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Max. Vorlaufzeit (Tage)
                        </label>
                        <input
                          type="number"
                          value={modalSettings.booking.max_advance_days}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              booking: {
                                ...prev.booking,
                                max_advance_days: Number(e.target.value) || 0,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          Wie weit im Voraus buchbar
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                          Min. Vorlaufzeit (Stunden)
                        </label>
                        <input
                          type="number"
                          value={modalSettings.booking.min_advance_hours}
                          onChange={(e) =>
                            setModalSettings((prev) => ({
                              ...prev,
                              booking: {
                                ...prev.booking,
                                min_advance_hours: Number(e.target.value) || 0,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-[var(--primary-color)] focus:border-[var(--primary-color)]"
                        />
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          Mindestzeit vor Buchungsbeginn
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-slate-600" />

                  <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                    Nutzungsarten
                  </h3>
                  {(modalSettings.booking.use_cases || []).length === 0 && (
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      Keine Nutzungsarten verfügbar.
                    </p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(modalSettings.booking.use_cases || []).map((useCase) => (
                      <div
                        key={useCase.id}
                        className="px-3 py-2.5 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 flex flex-wrap items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={!!useCase.enabled}
                              onChange={(e) =>
                                setModalSettings((prev) => ({
                                  ...prev,
                                  booking: {
                                    ...prev.booking,
                                    use_cases: (
                                      prev.booking.use_cases || []
                                    ).map((item) =>
                                      item.id === useCase.id
                                        ? {
                                            ...item,
                                            enabled: e.target.checked,
                                          }
                                        : item,
                                    ),
                                  },
                                }))
                              }
                            />
                            <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-color rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-color dark:peer-checked:bg-primary-color/40" />
                          </label>
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {useCase.name}
                          </span>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 shrink-0 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!useCase.approval_required}
                            onChange={(e) =>
                              setModalSettings((prev) => ({
                                ...prev,
                                booking: {
                                  ...prev.booking,
                                  use_cases: (prev.booking.use_cases || []).map(
                                    (item) =>
                                      item.id === useCase.id
                                        ? {
                                            ...item,
                                            approval_required: e.target.checked,
                                          }
                                        : item,
                                  ),
                                },
                              }))
                            }
                            className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-color focus:ring-primary-color dark:bg-slate-700"
                          />
                          Genehmigung
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {panelMode === "settings" && settingsModalTab === "locations" && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                      Zugewiesene Standorte
                    </h3>
                    <button
                      type="button"
                      onClick={handleAddLocationClick}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-color text-white text-sm font-medium hover:opacity-90 transition-opacity"
                    >
                      <Plus className="w-4 h-4" />
                      Standort hinzufügen
                    </button>
                  </div>
                  {tenantLocations.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-slate-400">
                      Keine Standorte zugewiesen.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {tenantLocations.map((loc) => {
                        const locId = loc.location_id || loc.id;
                        const roleLabels = {
                          owner: "Owner",
                          operator: "Operator",
                          partner: "Partner",
                          viewer: "Viewer",
                        };
                        return (
                          <div
                            key={locId}
                            className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600"
                          >
                            <div className="flex items-center gap-3">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                                {loc.location_name || loc.name || locId}
                              </span>
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-primary-color-tint primary-color">
                                {roleLabels[loc.role] || loc.role || "—"}
                              </span>
                              {loc.is_primary && (
                                <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                                  Primär
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveLocation(locId)}
                              disabled={locationAssigning}
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                              title="Entfernen"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <LocationAssignForm
                    assignedIds={tenantLocations.map(
                      (l) => l.location_id || l.id,
                    )}
                  />
                </div>
              )}
            </form>

            <div className="px-8 py-4 border-t border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-700/30 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={closePanel}
                disabled={saving}
                className="px-6 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                form="tenant-settings-form"
                disabled={saving}
                className="px-6 py-2.5 rounded-xl bg-primary-color text-white font-medium shadow-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Check className="w-5 h-5" />
                )}
                {settingsModalTab === "general"
                  ? "Speichern"
                  : "Einstellungen speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation – centered popup */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setDeleteConfirm(null)}
            aria-hidden
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:border dark:border-slate-700 max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
                  Mandant löschen?
                </h3>
                <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
                  „{deleteConfirm.name ?? "Unbekannt"}“ wird dauerhaft gelöscht.
                  Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 font-medium transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 dark:bg-red-500 text-white font-medium hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Tenant - centered modal with 2-step flow */}
      {showCreateModal && (
        <div
          className="modal-overlay active dark:bg-black/70"
          onClick={() => {
            if (createStep === "details") setShowCreateModal(false);
          }}
        >
          <div
            className="modal-container dark:bg-slate-800 dark:border-slate-700"
            style={{
              maxWidth: createStep === "locations" ? "640px" : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {createStep === "details"
                  ? "Mandant anlegen"
                  : "Standorte zuweisen"}
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {createStep === "details" && (
              <form onSubmit={handleCreateSubmit} className="p-6 space-y-4">
                {/* Template selector — backend `from_template_id` pre-fills
                    plan_id, settings, branding, timezone, description from
                    the picked tenant (typically the "Default Tenant" stub).
                    Any field the admin types here overrides the template. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Vorlage (optional)
                  </label>
                  <select
                    value={formData.from_template_id || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        from_template_id: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
                  >
                    <option value="">— Ohne Vorlage —</option>
                    {tenants.map((t) => (
                      <option
                        key={t.tenant_id || t.id}
                        value={t.tenant_id || t.id}
                      >
                        {t.name}
                        {t.slug ? ` (${t.slug})` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Erzeugt einen Stub mit den Standardwerten der gewählten
                    Vorlage (z.B. „Default Tenant“). Eigene Werte unten
                    überschreiben.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Subdomain
                  </label>
                  <input
                    type="text"
                    value={formData.subdomain}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        subdomain: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, slug: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Kontakt-E-Mail
                  </label>
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contact_email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Kontakttelefon
                  </label>
                  <input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        contact_phone: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
                  >
                    <option value="active">Aktiv</option>
                    <option value="inactive">Inaktiv</option>
                    <option value="suspended">Gesperrt</option>
                    <option value="demo">Demo</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Plan
                  </label>
                  <select
                    value={formData.plan_id || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        plan_id: e.target.value,
                      }))
                    }
                    disabled={saving || plans.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
                  >
                    <option value="">
                      {plans.length === 0
                        ? "Keine Pläne verfügbar"
                        : "Kein Plan zugewiesen"}
                    </option>
                    {plans.map((plan) => {
                      const optionId =
                        plan.id ??
                        plan.plan_id ??
                        plan.uuid ??
                        plan.key ??
                        null;
                      if (!optionId) return null;
                      const label =
                        plan.name ??
                        plan.plan_name ??
                        plan.code ??
                        String(optionId);
                      return (
                        <option key={optionId} value={String(optionId)}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Beschreibung
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent dark:placeholder-slate-400"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="create-is-active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 dark:border-slate-500"
                  />
                  <label
                    htmlFor="create-is-active"
                    className="text-sm font-medium text-gray-700 dark:text-slate-300"
                  >
                    Aktiv
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 disabled:opacity-50 inline-flex items-center"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : null}
                    Anlegen
                  </button>
                </div>
              </form>
            )}

            {createStep === "locations" && (
              <div className="p-6 space-y-4">
                <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-800 dark:text-green-200 font-medium flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Mandant erfolgreich erstellt! Sie können jetzt Standorte
                    zuweisen.
                  </p>
                </div>

                {tenantLocations.length > 0 && (
                  <div className="space-y-2">
                    {tenantLocations.map((loc) => {
                      const locId = loc.location_id || loc.id;
                      const roleLabels = {
                        owner: "Owner",
                        operator: "Operator",
                        partner: "Partner",
                        viewer: "Viewer",
                      };
                      return (
                        <div
                          key={locId}
                          className="flex items-center justify-between p-3 rounded-xl bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600"
                        >
                          <div className="flex items-center gap-3">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">
                              {loc.location_name || loc.name || locId}
                            </span>
                            <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-primary-color-tint primary-color">
                              {roleLabels[loc.role] || loc.role || "—"}
                            </span>
                            {loc.is_primary && (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                                Primär
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveLocation(locId)}
                            disabled={locationAssigning}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <LocationAssignForm
                  assignedIds={tenantLocations.map(
                    (l) => l.location_id || l.id,
                  )}
                />

                <div className="flex justify-end pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setCreateStep("details");
                      setCreatedTenantId(null);
                      setTenantLocations([]);
                      setFormData({
                        name: "",
                        subdomain: "",
                        slug: "",
                        contact_email: "",
                        contact_phone: "",
                        description: "",
                        is_active: true,
                        status: "active",
                        plan_id: "",
                      });
                    }}
                    className="px-4 py-2 bg-primary-color text-white rounded-lg hover:opacity-90 inline-flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Fertig
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
